import React, { useState } from "react";
import { CompanyAnalytics } from "../../../services/analyticsService";
import analyticsService from "../../../services/analyticsService";
import "./PricePerformanceSnapshot.scss";

interface PricePerformanceSnapshotProps {
  companyId: number;
  analytics: CompanyAnalytics | null;
  onRefresh?: () => void;
}

const PricePerformanceSnapshot: React.FC<PricePerformanceSnapshotProps> = ({ companyId, analytics, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    analyticsService.refreshPerformanceMetrics(companyId).subscribe({
      next: () => {
        if (onRefresh) onRefresh();
        setRefreshing(false);
      },
      error: (err) => {
        console.error("Failed to refresh performance metrics:", err);
        setRefreshing(false);
      }
    });
  };
  // Format volume for display
  const formatVolume = (volume: number | null | undefined): string => {
    if (!volume) return "N/A";
    if (volume >= 10000000) return `${(volume / 10000000).toFixed(2)}Cr`;
    if (volume >= 100000) return `${(volume / 100000).toFixed(2)}L`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(2)}K`;
    return volume.toString();
  };

  // Format market cap
  const formatMarketCap = (marketCap: number | null | undefined): string => {
    if (!marketCap) return "N/A";
    if (marketCap >= 100000) return `₹${(marketCap / 100000).toFixed(2)}L Cr`;
    if (marketCap >= 1000) return `₹${(marketCap / 1000).toFixed(2)}K Cr`;
    return `₹${marketCap.toFixed(2)} Cr`;
  };

  const performance = analytics?.performance;
  const currentPrice = performance?.current_price || 0;
  const dayChange = performance?.day_change || 0;
  const dayChangePercent = performance?.day_change_percent || 0;
  const isPositive = dayChange >= 0;

  const formatCurrency = (value: number) => {
    return `₹${value.toFixed(2)}`;
  };

  return (
    <div className="price-performance-snapshot section">
      <div className="is-flex is-justify-content-space-between is-align-items-center mb-5">
        <h2 className="title is-4 mb-0">Price & Performance Snapshot</h2>
        <button
          className={`button is-small is-info ${refreshing ? 'is-loading' : ''}`}
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh performance metrics"
        >
          <span className="icon is-small">
            <i className="fas fa-sync-alt"></i>
          </span>
          <span>Refresh</span>
        </button>
      </div>

      <div className="columns is-variable is-2">
        {/* Current Price Card */}
        <div className="column is-2">
          <div className="card snapshot-card">
            <div className="card-content">
              <p className="heading has-text-grey is-size-7">Current Price</p>
              <p className="subtitle is-7 has-text-grey-light mb-3">Baseline for entry/exit</p>
              <p className="title is-5 mb-1">
                {currentPrice ? formatCurrency(currentPrice) : 'N/A'}
              </p>
              <p className={`is-size-7 ${isPositive ? 'has-text-success' : 'has-text-danger'} has-text-weight-semibold`}>
                {isPositive ? '+' : ''}{formatCurrency(dayChange)} ({isPositive ? '+' : ''}{dayChangePercent.toFixed(2)}%)
              </p>
            </div>
          </div>
        </div>

        {/* Day Change Card */}
        <div className="column is-2">
          <div className="card snapshot-card">
            <div className="card-content">
              <p className="heading has-text-grey is-size-7">Day Change</p>
              <p className="subtitle is-7 has-text-grey-light mb-3">Short-term momentum</p>
              <p className={`title is-5 mb-1 ${isPositive ? 'has-text-success' : 'has-text-danger'}`}>
                {isPositive ? '+' : ''}{formatCurrency(dayChange)}
              </p>
              <p className={`is-size-7 ${isPositive ? 'has-text-success' : 'has-text-danger'} has-text-weight-semibold`}>
                ({isPositive ? '+' : ''}{dayChangePercent.toFixed(2)}%)
              </p>
            </div>
          </div>
        </div>

        {/* 52-Week High/Low Card */}
        <div className="column is-2">
          <div className="card snapshot-card">
            <div className="card-content">
              <p className="heading has-text-grey is-size-7">52-Week H/L</p>
              <p className="subtitle is-7 has-text-grey-light mb-2">Valuation check</p>
              <div className="mb-2">
                <p className="is-size-7 has-text-grey">H: {performance?.week_52_high ? formatCurrency(performance.week_52_high) : 'N/A'}</p>
                <p className="is-size-7 has-text-grey">L: {performance?.week_52_low ? formatCurrency(performance.week_52_low) : 'N/A'}</p>
              </div>
              {performance?.week_52_high && performance?.week_52_low && currentPrice && (
                <div className="range-bar">
                  <div
                    className="current-position"
                    style={{
                      left: `${((currentPrice - performance.week_52_low) /
                        (performance.week_52_high - performance.week_52_low)) * 100}%`
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Volume Card */}
        <div className="column is-2">
          <div className="card snapshot-card">
            <div className="card-content">
              <p className="heading has-text-grey is-size-7">Volume</p>
              <p className="subtitle is-7 has-text-grey-light mb-3">Trend strength</p>
              <div>
                <p className="is-size-7 has-text-grey mb-1">
                  Today: <span className="has-text-link has-text-weight-semibold">{formatVolume(performance?.volume_today)}</span>
                </p>
                <p className="is-size-7 has-text-grey">
                  Avg: <span className="has-text-weight-semibold">{formatVolume(performance?.volume_avg_30d)}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Market Cap Card */}
        <div className="column is-2">
          <div className="card snapshot-card">
            <div className="card-content">
              <p className="heading has-text-grey is-size-7">Market Cap</p>
              <p className="subtitle is-7 has-text-grey-light mb-3">Size & liquidity</p>
              <p className="title is-5">
                {formatMarketCap(analytics?.company?.market_cap)}
              </p>
            </div>
          </div>
        </div>

        {/* Beta / Volatility Card */}
        <div className="column is-2">
          <div className="card snapshot-card">
            <div className="card-content">
              <p className="heading has-text-grey is-size-7">Beta / Volatility</p>
              <p className="subtitle is-7 has-text-grey-light mb-3">Risk assessment</p>
              <p className="title is-5 mb-2">{performance?.beta?.toFixed(2) || 'N/A'}</p>
              <span className={`tag is-small volatility-badge ${performance?.volatility_category?.toLowerCase() || 'moderate'}`}>
                {performance?.volatility_category || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricePerformanceSnapshot;
